import { useState, type FormEvent } from 'react';
import { LifeBuoy, Loader2, Send, ArrowLeft, RefreshCw } from 'lucide-react';
import { Card, CardContent } from './card';
import { EmptyState } from './empty-state';
import { Skeleton } from './skeleton';
import { cn } from './cn';

/**
 * Chamados de suporte no web (admin + parceiro). Puramente apresentacional:
 * cada app injeta os dados via react-query com o seu próprio cliente HTTP
 * (`createApiClient`), e o kit garante que a UI não drifte entre os dois.
 *
 * Contrato da API: GET /support/tickets, GET /support/tickets/:id,
 * POST /support/tickets e POST /support/tickets/:id/messages.
 */

export interface SupportTicketSummary {
  id: string;
  subject: string;
  status: SupportTicketStatus;
  priority: string;
  /** true a partir da primeira resposta pública da equipe (firstResponseAt). */
  hasStaffReply: boolean;
  /** Última resposta pública da equipe (ISO) — base do aviso "nova resposta". */
  lastStaffReplyAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SupportTicketMessage {
  id: string;
  content: string;
  fromStaff: boolean;
  senderName: string | null;
  createdAt: string;
}

export interface SupportTicketDetail {
  id: string;
  subject: string;
  status: SupportTicketStatus;
  priority: string;
  createdAt: string;
  messages: SupportTicketMessage[];
}

export type SupportTicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';

const STATUS_META: Record<SupportTicketStatus, { label: string; className: string }> = {
  open: { label: 'Aberto', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  in_progress: { label: 'Em andamento', className: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  resolved: { label: 'Resolvido', className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  closed: { label: 'Fechado', className: 'bg-muted text-muted-foreground' },
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function SupportStatusBadge({ status }: { status: SupportTicketStatus }) {
  const meta = STATUS_META[status];
  return (
    <span className={cn('rounded-full px-2.5 py-1 text-xs font-medium', meta.className)}>
      {meta.label}
    </span>
  );
}

// ==========================================
// "Não vistos" — marcação local por navegador
// ==========================================

const SEEN_KEY = 'mustauto:support-seen';

/**
 * Guarda, por ticket, a data da última resposta da equipe que o usuário já
 * abriu. Não há estado de leitura no servidor (support_tickets não tem coluna
 * de lido), então a marca é por navegador: serve para destacar resposta nova,
 * nunca como fonte de verdade. Falha de storage degrada para "tudo visto".
 */
function readSeenMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function markSupportTicketSeen(ticketId: string, lastStaffReplyAt: string): void {
  try {
    const map = readSeenMap();
    const current = map[ticketId];
    if (current && current >= lastStaffReplyAt) return;
    map[ticketId] = lastStaffReplyAt;
    localStorage.setItem(SEEN_KEY, JSON.stringify(map));
  } catch {
    // storage indisponível (modo privado/quota) — sem marcação, sem quebrar
  }
}

/**
 * Tickets com resposta da equipe mais recente do que a última que este
 * navegador abriu. Datas ISO comparam lexicograficamente.
 */
export function unseenSupportReplyIds(tickets: SupportTicketSummary[]): string[] {
  const map = readSeenMap();
  return tickets
    .filter((t) => {
      if (!t.lastStaffReplyAt) return false;
      const seen = map[t.id];
      return !seen || seen < t.lastStaffReplyAt;
    })
    .map((t) => t.id);
}

// ==========================================
// Lista de chamados
// ==========================================

interface SupportTicketListProps {
  tickets: SupportTicketSummary[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onOpen: (ticketId: string) => void;
  onNew: () => void;
  /** ids com resposta ainda não aberta neste navegador (destaque visual). */
  unseenIds?: string[];
}

export function SupportTicketList({
  tickets,
  isLoading,
  isError,
  onRetry,
  onOpen,
  onNew,
  unseenIds = [],
}: SupportTicketListProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={<RefreshCw size={40} className="text-muted-foreground" />}
        title="Não foi possível carregar seus chamados"
        description="Verifique sua conexão e tente novamente."
        action={{ label: 'Tentar de novo', onClick: onRetry }}
      />
    );
  }

  if (tickets.length === 0) {
    return (
      <EmptyState
        icon={<LifeBuoy size={40} className="text-muted-foreground" />}
        title="Nenhum chamado ainda"
        description="Abra um chamado para falar com a equipe MustAuto."
        action={{ label: 'Novo chamado', onClick: onNew }}
      />
    );
  }

  const unseen = new Set(unseenIds);

  return (
    <div className="space-y-3">
      {tickets.map((ticket) => (
        <button
          key={ticket.id}
          type="button"
          onClick={() => onOpen(ticket.id)}
          className="block w-full text-left"
        >
          <Card className="transition-colors hover:border-primary/40">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-foreground">
                    {ticket.subject}
                  </span>
                  {unseen.has(ticket.id) && (
                    <span className="shrink-0 rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-600 dark:text-red-400">
                      Nova resposta
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Atualizado em {formatDateTime(ticket.updatedAt)}
                  {ticket.hasStaffReply && ' · respondido'}
                </p>
              </div>
              <SupportStatusBadge status={ticket.status} />
            </CardContent>
          </Card>
        </button>
      ))}
    </div>
  );
}

// ==========================================
// Thread de um chamado
// ==========================================

interface SupportTicketThreadProps {
  ticket: SupportTicketDetail | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onBack: () => void;
  onSend: (content: string) => Promise<void>;
  isSending: boolean;
}

export function SupportTicketThread({
  ticket,
  isLoading,
  isError,
  onRetry,
  onBack,
  onSend,
  isSending,
}: SupportTicketThreadProps) {
  const [draft, setDraft] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || isSending) return;
    await onSend(content);
    setDraft('');
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError || !ticket) {
    return (
      <EmptyState
        icon={<RefreshCw size={40} className="text-muted-foreground" />}
        title="Não foi possível carregar o chamado"
        description="Verifique sua conexão e tente novamente."
        action={{ label: 'Tentar de novo', onClick: onRetry }}
        secondaryAction={{ label: 'Voltar', onClick: onBack }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft size={16} />
        Meus chamados
      </button>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-foreground">{ticket.subject}</h1>
        <SupportStatusBadge status={ticket.status} />
      </div>

      <div className="space-y-3">
        {ticket.messages.map((msg) => (
          <div
            key={msg.id}
            className={cn('flex', msg.fromStaff ? 'justify-start' : 'justify-end')}
          >
            <div
              className={cn(
                'max-w-[85%] rounded-lg px-4 py-3 md:max-w-[70%]',
                msg.fromStaff
                  ? 'bg-muted text-foreground'
                  : 'bg-primary text-primary-foreground',
              )}
            >
              <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
              <p
                className={cn(
                  'mt-1.5 text-[11px]',
                  msg.fromStaff ? 'text-muted-foreground' : 'text-primary-foreground/70',
                )}
              >
                {msg.fromStaff ? (msg.senderName ?? 'Suporte MustAuto') : 'Você'} ·{' '}
                {formatDateTime(msg.createdAt)}
              </p>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          maxLength={5000}
          placeholder="Escreva sua resposta…"
          aria-label="Resposta ao chamado"
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
        />
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!draft.trim() || isSending}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {isSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Enviar
          </button>
        </div>
      </form>
    </div>
  );
}

// ==========================================
// Novo chamado
// ==========================================

export interface NewSupportTicketInput {
  subject: string;
  body: string;
  priority: 'low' | 'normal' | 'high';
}

interface NewSupportTicketFormProps {
  onSubmit: (input: NewSupportTicketInput) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const MIN_BODY_LENGTH = 10;

export function NewSupportTicketForm({
  onSubmit,
  onCancel,
  isSubmitting,
}: NewSupportTicketFormProps) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<NewSupportTicketInput['priority']>('normal');

  const canSubmit =
    subject.trim().length > 0 && body.trim().length >= MIN_BODY_LENGTH && !isSubmitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    await onSubmit({ subject: subject.trim(), body: body.trim(), priority });
    setSubject('');
    setBody('');
    setPriority('normal');
  };

  const inputClass =
    'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none';

  return (
    <Card>
      <CardContent className="p-4">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="support-subject" className="mb-1 block text-xs font-medium text-muted-foreground">
              Assunto
            </label>
            <input
              id="support-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={300}
              placeholder="Ex.: Não consigo emitir o relatório"
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="support-body" className="mb-1 block text-xs font-medium text-muted-foreground">
              Descrição
            </label>
            <textarea
              id="support-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              maxLength={5000}
              placeholder="Conte o que aconteceu, com o máximo de detalhes."
              className={inputClass}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Mínimo de {MIN_BODY_LENGTH} caracteres.
            </p>
          </div>

          <div>
            <label htmlFor="support-priority" className="mb-1 block text-xs font-medium text-muted-foreground">
              Prioridade
            </label>
            <select
              id="support-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as NewSupportTicketInput['priority'])}
              className={inputClass}
            >
              <option value="low">Baixa</option>
              <option value="normal">Normal</option>
              <option value="high">Alta</option>
            </select>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              Abrir chamado
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
